import React from 'react';
import { APPLICATIONS } from '@blocs.auth';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { SocialMediaButton } from '@minecraft.molecules';
import { useDeviceAndBrowserInfo } from '@minecraft.utils';
import { getEnvironment } from '@minecraft.environment';

type ReadonlyInstagramBtn = {
  readOnly: true;
  label: string;
  profileLink: string;
  onConnect?: never;
  onDisconnect?: never;
  showCloseButton?: never;
};

type WriteableInstagramBtn = {
  readOnly?: false;
  label: string;
  profileLink: string;
  onConnect: () => void;
  onDisconnect: () => void;
  showCloseButton?: boolean;
};

interface InstagramButtonClick {
  profileLink: string;
  onConnect: () => void;
  readOnly: boolean;
  isCasting: boolean;
  deviceInfo: string;
  browserInfo: string;
}

const handleInstagramButtonClick = ({
  profileLink,
  onConnect,
  readOnly,
  isCasting,
  deviceInfo,
  browserInfo,
}: InstagramButtonClick) => {
  if (profileLink) {
    window.open(profileLink, '_blank', 'noopener,noreferrer');
    if (isCasting) {
      const data = {
        timestamp: new Date().toISOString(),
        device: deviceInfo,
        browser: browserInfo,
      };
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.CD_INSTAGRAM_CLICK_TALENT_PROFILE,
        data,
      });
    }
  } else if (!readOnly) {
    onConnect();
  }
};

type InstagramButtonProps = ReadonlyInstagramBtn | WriteableInstagramBtn;

export const InstagramButton = ({
  label,
  profileLink,
  onConnect,
  onDisconnect,
  showCloseButton = false,
  readOnly = false,
}: InstagramButtonProps) => {
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const environment = getEnvironment();
  const appName = environment?.AUDIENCE;
  const isCasting = appName === APPLICATIONS.casting;

  return (
    <SocialMediaButton
      id="connect-instagram"
      label={label}
      aria-label={label}
      iconName="profile-social-instagram"
      isLarge
      wrapperClassName="cn_atom_p-1"
      onClick={() =>
        handleInstagramButtonClick({ profileLink, onConnect, readOnly, isCasting, deviceInfo, browserInfo })
      }
      onCloseClick={showCloseButton ? onDisconnect : undefined}
    />
  );
};
